/**
 * The bot's per-language input words, mirrored from `echoflicks-whatsapp-bot`
 * (RU-106 / RU-107 / RU-500). Data only: the matching rules live in
 * `botButtonIntents.ts`, the folding in `intentNormalisation.ts`.
 *
 * Each catalog (`bot/i18n/catalogs/<code>.yaml`) holds a pipe-separated list
 * of WHOLE-message phrases per key; `bot/i18n/vocabulary.py` reads every
 * catalog, whatever the contact's language, and drops the phrases the English
 * source (`BotMessages`) already lists — English keeps its own matchers. The
 * functions below apply the same rule to the same values.
 *
 * Only `ru` ships STOP/START words; every other catalog ships "" so everyday
 * words like "baja" or "parar" can never opt anyone out.
 *
 * Source: echoflicks-whatsapp-bot origin/main e25aa4c (the vocabularies came
 * with a2c93d4, #524); wave D (bs mk sv da hu sl) from c415549d (#541), which
 * left every other catalog's values unchanged. `botButtonIntents.test.ts` pins
 * the values that matter to an operator, so a drift fails a test instead of
 * mislabelling a button.
 */

import { normalizeIntentText, splitPhrases } from "./intentNormalisation";

export type VocabularyKey =
  | "CREATE_INTENT_PHRASES"
  | "COMMAND_ALIASES_CREATE"
  | "DONE_KEYWORDS"
  | "STOP_KEYWORDS"
  | "START_KEYWORDS";

/** `BotMessages` — what English already accepts. */
const ENGLISH_VOCABULARY: Record<VocabularyKey, string> = {
  CREATE_INTENT_PHRASES:
    "create|create my movie|create movie|start my movie|create my echo|create echo|make my movie|create a new echo|I bought my Echo, let's go",
  COMMAND_ALIASES_CREATE: "create",
  DONE_KEYWORDS: "done",
  STOP_KEYWORDS: "stop|unsubscribe|stop promotions",
  START_KEYWORDS: "start|subscribe|resume|unstop",
};

/** Catalog values by language code; a code that is absent ships "". */
const CATALOG_VOCABULARY: Record<VocabularyKey, Record<string, string>> = {
  CREATE_INTENT_PHRASES: {
    bg: "създай echo|създай моето echo|ново echo|направи филм",
    bs: "napravi echo|napravi moj echo|novi echo|napravi film|napravi moj film|kreiraj echo",
    cs: "vytvořit echo|vytvořit moje echo|nové echo|udělat film",
    da: "lav et echo|nyt echo|lav en film|lav mit echo|lav min film|lav et nyt echo|opret et echo|opret mit echo|opret et nyt echo",
    de: "echo erstellen|mein echo erstellen|neues echo|ein neues echo erstellen|meinen film erstellen",
    el: "δημιουργία echo|δημιούργησε το echo μου|νέο echo|φτιάξε ταινία",
    es: "crear echo|crear un echo|crear nuevo echo|crear un nuevo echo|crear mi echo|nuevo echo|crear mi película",
    fr: "créer un echo|créer mon echo|nouvel echo|créer un nouvel echo|créer mon film",
    hr: "stvori echo|stvori moj echo|novi echo|napravi film",
    hu: "új echo|echo készítése|készíts filmet|készíts echót|készíts egy echót|új echót kérek|új filmet kérek",
    it: "crea echo|crea il mio echo|crea un echo|crea un nuovo echo|nuovo echo|crea il mio film",
    mk: "создај|создај echo|создај го мојот echo|создај филм|нов echo|направи echo|направи филм",
    nl: "echo maken|maak mijn echo|nieuwe echo|maak een nieuwe echo|maak mijn film",
    pl: "utwórz echo|stwórz echo|stwórz moje echo|nowe echo|zrób film",
    pt: "criar echo|criar um echo|criar o meu echo|novo echo|criar um novo echo|criar o meu filme",
    ro: "creează echo|creează echo-ul meu|echo nou|fă un film",
    ru: "создать|новое echo|сделать фильм|создать echo",
    sl: "ustvari echo|ustvari moj echo|ustvari film|ustvari moj film|nov echo|naredi echo|naredi film|naredi moj film",
    sq: "krijo echo|krijo echo-n tim|echo i ri|bëj film",
    sr: "napravi echo|napravi moj echo|novi echo|napravi film|направи echo|нови echo",
    sv: "skapa echo|skapa mitt echo|skapa ett echo|skapa ett nytt echo|nytt echo|skapa min film|gör en film|gör min film",
    uk: "створити echo|створи echo|нове echo|зробити фільм",
  },
  COMMAND_ALIASES_CREATE: {
    bg: "създай",
    bs: "napravi|kreiraj",
    cs: "vytvořit",
    da: "opret",
    de: "erstellen",
    el: "δημιουργία",
    es: "crear",
    fr: "créer",
    hr: "stvori",
    hu: "új echo",
    it: "crea",
    mk: "создај",
    nl: "maken",
    pl: "utwórz",
    pt: "criar",
    ro: "creează",
    ru: "создать",
    sl: "ustvari echo",
    sq: "krijo",
    sr: "napravi|направи",
    sv: "skapa",
    uk: "створити",
  },
  // Honoured only while the contact is uploading photos (main.py
  // `is_done_reply`), never as a broadcast button.
  DONE_KEYWORDS: {
    bg: "готово",
    bs: "gotovo|završeno",
    cs: "hotovo",
    da: "færdig|jeg er færdig",
    de: "fertig|erledigt",
    el: "έτοιμο|τέλος",
    es: "listo|hecho|terminado|ya está",
    fr: "terminé|fini|c'est fini",
    hr: "gotovo",
    hu: "kész|készen vagyok|befejeztem",
    it: "fatto|finito|ho finito",
    mk: "готово|завршив",
    nl: "klaar|gedaan",
    pl: "gotowe|zrobione",
    pt: "pronto|feito|terminei|já está",
    ro: "gata|am terminat",
    ru: "готово|всё|дальше",
    sl: "končano|konec",
    sq: "mbarova|u bë",
    sr: "gotovo|готово",
    sv: "färdig|jag är klar|jag är färdig",
    uk: "готово|далі",
  },
  STOP_KEYWORDS: {
    ru: "стоп|отписаться|отписка|отпишите меня",
  },
  // Re-subscribes a contact who opted out; does nothing for anyone else.
  START_KEYWORDS: {
    ru: "старт|подписаться",
  },
};

/**
 * The bot's own create-button titles (`CREATE_ECHO_BUTTON_TITLE`,
 * `CREATE_NEW_ECHO_BUTTON_TITLE`), English first, then every catalog. A
 * template quick reply posts back its title, so the bot accepts each of them
 * on both delivery paths (`create_intent_phrases`).
 */
const CREATE_BUTTON_TITLES: Record<string, readonly string[]> = {
  en: ["🎬 Create my Echo", "Create new Echo"],
  bg: ["🎬 Създай моето Echo", "Ново Echo"],
  bs: ["🎬 Napravi moj Echo", "Napravi novi Echo"],
  cs: ["🎬 Vytvořit moje Echo", "Nové Echo"],
  da: ["🎬 Lav mit Echo", "Lav et nyt Echo"],
  de: ["🎬 Echo erstellen", "Neues Echo machen"],
  el: ["🎬 Φτιάξε το Echo μου", "Νέο Echo"],
  es: ["🎬 Crear mi Echo", "Crear otro Echo"],
  fr: ["🎬 Créer mon Echo", "Créer un autre Echo"],
  hr: ["🎬 Napravi moj Echo", "Novi Echo"],
  hu: ["🎬 Echo készítése", "Új Echo készítése"],
  it: ["🎬 Crea il mio Echo", "Crea un nuovo Echo"],
  mk: ["🎬 Создај Echo", "Создај нов Echo"],
  nl: ["🎬 Maak mijn Echo", "Nieuwe Echo maken"],
  pl: ["🎬 Stwórz moje Echo", "Nowe Echo"],
  pt: ["🎬 Criar o meu Echo", "Criar outro Echo"],
  ro: ["🎬 Creează Echo-ul", "Echo nou"],
  ru: ["🎬 Создать моё Echo", "Новое Echo"],
  sl: ["🎬 Ustvari moj Echo", "Ustvari nov Echo"],
  sq: ["🎬 Krijo Echo-n tim", "Krijo Echo të ri"],
  sr: ["🎬 Napravi moj Echo", "Novi Echo"],
  sv: ["🎬 Skapa mitt Echo", "Skapa nytt Echo"],
  uk: ["🎬 Створити моє Echo", "Нове Echo"],
};

/** The English source list for `key`, normalised. */
export function englishPhrases(key: VocabularyKey): ReadonlySet<string> {
  return new Set(splitPhrases(ENGLISH_VOCABULARY[key]));
}

/** `{code: phrases}` for every catalog that lists words for `key`. */
export function phrasesByLanguage(
  key: VocabularyKey,
): Readonly<Record<string, readonly string[]>> {
  const found: Record<string, string[]> = {};
  for (const [code, value] of Object.entries(CATALOG_VOCABULARY[key])) {
    const phrases = splitPhrases(value);
    if (phrases.length) found[code] = phrases;
  }
  return found;
}

/** Every catalog's phrases for `key`, minus the English source's. */
export function localizedPhrases(key: VocabularyKey): ReadonlySet<string> {
  const english = englishPhrases(key);
  const localized = new Set<string>();
  for (const phrases of Object.values(phrasesByLanguage(key))) {
    for (const phrase of phrases) {
      if (!english.has(phrase)) localized.add(phrase);
    }
  }
  return localized;
}

/** Normalised create-button titles of English and every catalog. */
export function createButtonPhrases(): ReadonlySet<string> {
  const phrases = new Set<string>();
  for (const titles of Object.values(CREATE_BUTTON_TITLES)) {
    for (const title of titles) {
      const normalized = normalizeIntentText(title);
      if (normalized) phrases.add(normalized);
    }
  }
  return phrases;
}
